import { CSSProperties } from "react";

/**
 * `-webkit-text-security` is non-standard, so it is absent from csstype's
 * `CSSProperties` — hence the cast. Supported by Chrome, Safari, Edge and
 * Firefox 114+.
 * https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/-webkit-text-security
 */
const CSS_TEXT_MASK = { WebkitTextSecurity: "disc" } as CSSProperties;

/**
 * Masking with CSS only works where the property is implemented. Everywhere
 * else we fall back to `type="password"` rather than reveal the value: an
 * autofill annoyance is preferable to leaking the field in plaintext.
 */
const SUPPORTS_CSS_TEXT_MASK =
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("-webkit-text-security", "disc");

interface SensitiveFieldMaskProps {
  type: "text" | "password";
  autoComplete: string;
  /**
   * A plain style object, deliberately narrower than MUI's `SxProps<Theme>`:
   * callers merge it into their own `sx` by spreading, and `SxProps` also admits
   * function and array forms, which would spread to `{}` and silently drop the
   * mask. Kept as a partial so the unmasked case can return `{}`.
   */
  sx: { "& input"?: CSSProperties };
}

/**
 * Props that mask a **non-credential** sensitive field (Tax ID, SSN) without
 * exposing it to the browser's password manager.
 *
 * A field rendered as `type="password"` is claimed by Chrome's password manager
 * no matter what `autocomplete` says: it autofills the saved credential for the
 * site and offers to generate a strong password. Masking with CSS on a
 * `type="text"` input keeps the field out of that machinery entirely. See
 * ON-3412.
 *
 * Do NOT use for real password fields — those must stay `type="password"` so
 * the password manager can participate.
 */
export function getSensitiveFieldMaskProps(
  isMasked: boolean,
): SensitiveFieldMaskProps {
  const isPasswordInput = isMasked && !SUPPORTS_CSS_TEXT_MASK;

  return {
    type: isPasswordInput ? "password" : "text",
    // Chrome ignores `autocomplete="off"` on a password input when it holds a
    // saved credential for the site; `new-password` is the only value it
    // honours. On a text input `off` is correct — `new-password` there would
    // invite the password manager back in.
    autoComplete: isPasswordInput ? "new-password" : "off",
    sx: isMasked && !isPasswordInput ? { "& input": CSS_TEXT_MASK } : {},
  };
}
